/**
 * useSimulationSharedAccounts — fetches shared accounts from backend API.
 *
 * Returns { accounts, loading, error } — no hardcoded data.
 * Accounts are READ ONLY: readOnly flag is always true.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchSharedAccounts, SimulationAccount } from '../services/simulationService';

const POLL_INTERVAL_MS = 10_000;

export interface UseSharedAccountsResult {
  accounts: SimulationAccount[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useSimulationSharedAccounts(): UseSharedAccountsResult {
  const [accounts, setAccounts] = useState<SimulationAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const firstLoadRef = useRef<boolean>(true);
  const inFlightRef = useRef<boolean>(false);

  const load = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (firstLoadRef.current) setLoading(true);
    setError(null);
    try {
      const data = await fetchSharedAccounts();
      setAccounts(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load shared accounts';
      // Don't clobber a good render with a transient poll error.
      if (firstLoadRef.current) setError(msg);
      console.error('useSimulationSharedAccounts error:', msg);
    } finally {
      if (firstLoadRef.current) {
        setLoading(false);
        firstLoadRef.current = false;
      }
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh card balances + trade counts every 10s so the UI stays
  // in lock-step with the autopilot.
  useEffect(() => {
    const id = window.setInterval(() => { load(); }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  return { accounts, loading, error, refetch: load };
}
